// ═══════════════════════════════════════════════════════════════════════════
// Extreme-FCR Outlier Detection
// ───────────────────────────────────────────────────────────────────────────
// Detects implausible observed-FCR values across inter-sample periods so they
// do not silently propagate into cumulative metrics or missing-fish estimates.
//
// Handling contract (documented for downstream consumers):
//   • IMPLAUSIBLE  (FCR < 0, FCR < 0.3, FCR > 5.0) → flagged AND excluded from
//     observed-FCR aggregates. These values are almost always data-entry errors
//     (duplicated feed, wrong biomass entry, missed harvest) and averaging them
//     in would bias the missing-fish estimate.
//   • HIGH OUTLIER (3.0 ≤ FCR ≤ 5.0)              → flagged BUT kept. Real-world
//     cages can sustain FCR in this range under disease / overfeeding; excluding
//     them would mask the true signal.
//   • NEGATIVE     (biomass decreased while feed was given) → flagged as its own
//     type; typically indicates unreported mortality/harvest.
//
// Thresholds are based on published feed-conversion ranges for fin-fish
// aquaculture (salmonids ~1.0–1.4, tilapia ~1.2–1.8, sea bream ~1.3–2.0,
// trout ~1.0–1.5). A cumulative FCR below 0.3 is not biologically attainable;
// above 5.0 is implausible even under starvation + wastage.
// ═══════════════════════════════════════════════════════════════════════════

export const FCR_OUTLIER_THRESHOLDS = Object.freeze({
  MIN_PLAUSIBLE: 0.3,        // Below this → implausible (exclude)
  MAX_PLAUSIBLE: 5.0,        // Above this → implausible (exclude)
  HIGH_OUTLIER_FROM: 3.0,    // 3.0–5.0 → high outlier (keep, flag)
});

/**
 * Classify a single FCR value.
 * @param {number|null} fcr
 * @param {boolean} biomassDecreased  biomass gain was <= 0 while feed was given
 * @returns {'ok'|'implausible_low'|'implausible_high'|'negative'|'high_outlier'|null}
 */
export function classifyFCRValue(fcr, biomassDecreased = false) {
  if (biomassDecreased) return 'negative';
  if (fcr == null || !isFinite(fcr)) return null;
  if (fcr < 0) return 'negative';
  if (fcr < FCR_OUTLIER_THRESHOLDS.MIN_PLAUSIBLE) return 'implausible_low';
  if (fcr > FCR_OUTLIER_THRESHOLDS.MAX_PLAUSIBLE) return 'implausible_high';
  if (fcr >= FCR_OUTLIER_THRESHOLDS.HIGH_OUTLIER_FROM) return 'high_outlier';
  return 'ok';
}

/**
 * Scan reconciliation rows for implausible observed-FCR values between
 * consecutive samples. Each row is expected to carry `cumulative_feed_kg` and
 * `actual_biomass_kg`.
 *
 * Returns a summary + the set of excluded row indices so cumulative
 * aggregators can skip them.
 *
 * @param {Array<Object>} reconciliationRows  rows with cumulative_feed_kg and actual_biomass_kg
 * @returns {{
 *   count: number,
 *   implausibleCount: number,
 *   highOutlierCount: number,
 *   negativeCount: number,
 *   excludedIndices: Set<number>,
 *   details: Array<{
 *     fromIndex: number,
 *     toIndex: number,
 *     fcr: number|null,
 *     classification: string,
 *     feedKg: number,
 *     biomassGainKg: number,
 *   }>,
 *   hasExtremeFCR: boolean,
 * }}
 */
export function detectFCROutliers(reconciliationRows) {
  const result = {
    count: 0,
    implausibleCount: 0,
    highOutlierCount: 0,
    negativeCount: 0,
    excludedIndices: new Set(),
    details: [],
    hasExtremeFCR: false,
  };

  if (!Array.isArray(reconciliationRows) || reconciliationRows.length < 2) {
    return result;
  }

  for (let i = 1; i < reconciliationRows.length; i++) {
    const prev = reconciliationRows[i - 1];
    const curr = reconciliationRows[i];

    const feed = (curr?.cumulative_feed_kg ?? null) != null && (prev?.cumulative_feed_kg ?? null) != null
      ? curr.cumulative_feed_kg - prev.cumulative_feed_kg
      : null;
    const biomassGain = (curr?.actual_biomass_kg ?? null) != null && (prev?.actual_biomass_kg ?? null) != null
      ? curr.actual_biomass_kg - prev.actual_biomass_kg
      : null;

    if (feed == null || biomassGain == null || feed <= 0) continue;

    const biomassDecreased = biomassGain <= 0;
    const fcr = biomassDecreased ? null : feed / biomassGain;
    const classification = classifyFCRValue(fcr, biomassDecreased);

    if (!classification || classification === 'ok') continue;

    result.count += 1;
    if (classification === 'negative') result.negativeCount += 1;
    else if (classification === 'high_outlier') result.highOutlierCount += 1;
    else result.implausibleCount += 1;

    // Implausible or negative → exclude from aggregates; high_outlier stays in.
    if (classification !== 'high_outlier') {
      result.excludedIndices.add(i);
    }

    result.details.push({
      fromIndex: i - 1,
      toIndex: i,
      fromDate: prev?.date || prev?.sample_date || null,
      toDate: curr?.date || curr?.sample_date || null,
      fcr: fcr != null && isFinite(fcr) ? Math.round(fcr * 100) / 100 : null,
      classification,
      feedKg: Math.round(feed * 100) / 100,
      biomassGainKg: Math.round(biomassGain * 100) / 100,
    });
  }

  result.hasExtremeFCR = result.implausibleCount > 0 || result.negativeCount > 0;
  return result;
}
